import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { Tag } from "@/lib/types";
import { deleteTag, getTag } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showApiError, showSuccess } from "@/lib/toast";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { PageLayout } from "@/components/PageLayout";

export const TagPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  useBreadcrumbs([{ name: t("entities.tag"), path: ROUTES.TAG }]);

  const { goToTagAdd, goToTagDetail } = useAppNavigate();

  const columns: ColumnDef<Tag>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <PermissionChecker2 entity="Tag" rights={["GetDetails"]}>
            <div className="flex items-center justify-end gap-2">
              <UpdateRowButton onClickUpdate={() => goToTagDetail(row.original.id)} />
            </div>
          </PermissionChecker2>
        );
      },
    },
  ];

  const queryClient = useQueryClient();
  const deleteTagsMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteTag(id, String(activeFirm?.firmGuid))));
    },
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getTag"] });
    },
    onError: showApiError,
  });

  return (
    <PageLayout
      title={<>{t("entities.tag")}</>}
      description={t("tag.tagDesc")}
      extraRight={
        <PermissionChecker2 entity="Tag" rights={["Add"]}>
          <AddRowButton onClickAdd={goToTagAdd} title={t("common.addNewEntity", { entity: t("entities.tag") })} />
        </PermissionChecker2>
      }
    >
      <AxDataTable
        columns={columns}
        fetcher={getTag}
        getRowId={(row) => row.id}
        dynamicRequestParams={{ Firm_Id: String(activeFirm?.firmGuid) }}
        uniqQueryKey="getTag"
        onDeleteRows={deleteTagsMutation.mutateAsync}
        deleting={deleteTagsMutation.isPending}
      />
    </PageLayout>
  );
};
